import { badgeProgression, getBadgeProgressionStepForBlocks } from "@bbb/model";

export type BlockBadgeProgressSnapshot = {
  previousBlocks: number;
  currentBlocks: number;
  rewardBlocks?: number;
};

export type BlockBadgeProgressModel = {
  currentBlocks: number;
  rewardBlocks: number;
  headline: string;
  detail: string;
  progressLabel: string;
  progressPercent: number;
  previousBadgeName: string;
  currentBadgeName: string | null;
  nextBadgeName: string | null;
  unlockedBadgeName: string | null;
};

function formatBlocks(blocks: number) {
  return `${blocks.toLocaleString()} ${blocks === 1 ? "Block" : "Blocks"}`;
}

function safeBlocks(blocks: number) {
  return Math.max(0, Math.floor(Number(blocks) || 0));
}

export function buildBlockBadgeProgress(
  snapshot: BlockBadgeProgressSnapshot,
): BlockBadgeProgressModel {
  const previousBlocks = safeBlocks(snapshot.previousBlocks);
  const currentBlocks = safeBlocks(snapshot.currentBlocks);
  const rewardBlocks =
    snapshot.rewardBlocks ?? Math.max(0, currentBlocks - previousBlocks);
  const previousStep = getBadgeProgressionStepForBlocks(previousBlocks);
  const currentStep = getBadgeProgressionStepForBlocks(currentBlocks);
  const nextStep =
    badgeProgression.find((step) => step.minBlocks > currentBlocks) ?? null;
  const unlockedStep =
    currentStep && currentStep.badgeTier !== previousStep?.badgeTier
      ? currentStep
      : null;

  const segmentStart = currentStep?.minBlocks ?? 0;
  const segmentEnd = nextStep?.minBlocks ?? currentBlocks;
  const segmentSpan = Math.max(1, segmentEnd - segmentStart);
  const segmentProgress = Math.max(0, currentBlocks - segmentStart);
  const progressPercent = nextStep
    ? Math.min(100, (segmentProgress / segmentSpan) * 100)
    : 100;

  if (unlockedStep) {
    return {
      currentBlocks,
      rewardBlocks,
      headline: `${unlockedStep.achievementBadgeName} unlocked`,
      detail: previousStep
        ? `You advanced from ${previousStep.badgeTier} to ${unlockedStep.badgeTier}.`
        : `Your first Block moved you from no badge to ${unlockedStep.badgeTier}.`,
      progressLabel: nextStep
        ? `${formatBlocks(currentBlocks)} / ${nextStep.thresholdLabel}`
        : `${formatBlocks(currentBlocks)} total`,
      progressPercent,
      previousBadgeName: previousStep?.achievementBadgeName ?? "No badge",
      currentBadgeName: unlockedStep.achievementBadgeName,
      nextBadgeName: nextStep?.achievementBadgeName ?? null,
      unlockedBadgeName: unlockedStep.achievementBadgeName,
    };
  }

  const blocksRemaining = nextStep
    ? Math.max(0, nextStep.minBlocks - currentBlocks)
    : 0;

  return {
    currentBlocks,
    rewardBlocks,
    headline:
      currentStep && nextStep
        ? `${currentStep.badgeTier} to ${nextStep.badgeTier}`
        : "Badge progress updated",
    detail: nextStep
      ? `${formatBlocks(blocksRemaining)} until ${nextStep.achievementBadgeName}.`
      : "You have reached the highest badge tier.",
    progressLabel: nextStep
      ? `${formatBlocks(currentBlocks)} / ${nextStep.thresholdLabel}`
      : `${formatBlocks(currentBlocks)} total`,
    progressPercent,
    previousBadgeName: previousStep?.achievementBadgeName ?? "No badge",
    currentBadgeName: currentStep?.achievementBadgeName ?? null,
    nextBadgeName: nextStep?.achievementBadgeName ?? null,
    unlockedBadgeName: null,
  };
}
